<template>
  <div class="container-fluid">
    <confirm-dialog ref="confirmDialog"></confirm-dialog>
    <!-- ----------------------------------------------------------------------- -->
    <!-- OVERLAY: character sheet panel NB: pass key to force re-render/re-mount -->
    <!-- ----------------------------------------------------------------------- -->
    <CharacterSheetView
      class="bb-character-sheet-overlay"
      v-if="isSheetOpen"
      :character="character"
      :character_idx="character_idx"
      :list-scope="listScope"
      :key="sheetOpenCounter"
      @close-character-sheet="handleSheetClose"
      @save-character-sheet="handleCharacterSave"
      @delete-character-sheet="handleCharacterDelete"
    />
    <!-- ------------------------ -->
    <!-- NOTIFICATIONS            -->
    <!-- ------------------------ -->
    <notifications
      width="500"
      group="bb-confirm"
      :speed="100"
      :duration="1000"
      position="top right"
      classes="vue-notification"
    />
    <!-- ------------------- -->
    <!-- MAIN ROW            -->
    <!-- ------------------- -->
    <div class="row">
      <!-- ------------------- -->
      <!-- COL1: buttons panel -->
      <!-- ------------------- -->
      <div class="col-md-1 mt-1 px-3">
        <CharacterButtonsPanel
          :allow-add="allowCharacterAdd"
          @add-character="handleCharacterAdd"
          @close-character="handleCharactersClose"
        />
      </div>
      <!-- --------------------- -->
      <!-- COL2: character table -->
      <!-- --------------------- -->
      <div class="col-md-9">
        <TableCharacters
          v-if="characters && loadReady"
          :headings="tableHeadings"
          :characters="characters"
          :can-deploy-characters="canDeployCharacters"
          @update-character="handleCharacterUpdate"
          @delete-character="handleCharacterDelete"
          @deploy-character="handleCharacterDeploy"
        />
      </div>
    </div>
    <div
      v-if="showLoader"
      class="bb-loader"
    ></div>
  </div>
</template>

<script>
//========================================================
// scripts
//---------
import { inject } from 'vue'
import { axios } from '@/common/api.service.js'
import { mapActions, mapGetters, mapMutations } from 'vuex'

import useCharacterChangesHarness from '../composables/useCharacterChangesHarness'
import useDeleteCharacter from '../composables/useDeleteCharacter'
import useEditCombatant from '../composables/useEditCombatant'

import CharacterSheetView from '../views/CharacterSheetView.vue'
import CharacterButtonsPanel from '../components/CharacterButtonsPanel.vue'
import TableCharacters from '../components/TableCharacters.vue'
import ConfirmDialog from '../components/ConfirmDialog.vue'

import loadCharacterCharacteristicValues from '../helpers/loadCharacterCharacteristicValues'
import loadCharacterCharacteristicProps from '../helpers/loadCharacterCharacteristicProps'
import refreshCharacterOrder from '../helpers/refreshCharacterOrder'
import getCharacterByIndex from '../helpers/getCharacterByIndex'
import convertCharacterToCombatant from '../helpers/convertCharacterToCombatant'
import extractPlayerNames from '../helpers/extractPlayerNames'
import showNotification from '../helpers/showNotification'
import handleError from '../helpers/handleError'
import isEmpty from '../helpers/isEmpty'
import resolveCharacterForCombatDeploy from '../helpers/resolveCharacterForCombatDeploy'
import capitaliseMultipleWords from '../helpers/capitaliseMultipleWords'

export default {
  name: 'CharactersView',
  props: {
    listScope: {
      type: String,
      default: 'campaign',
    },
  },
  //========================================================
  // setup
  //-------
  setup() {
    const global = inject('global')
    return { global }
  },
  //========================================================
  // components
  //------------
  components: {
    TableCharacters,
    CharacterButtonsPanel,
    CharacterSheetView,
    ConfirmDialog,
  },
  //========================================================
  // data
  //------
  data() {
    return {
      sheetOpenCounter: 0,
      endpoint: '',
      local_slug: '',

      character_idx: 0,

      deleteInd: false,
      loadReady: false,
      initialLoad: true,
      isSheetOpen: false,
      openingCharacterSlug: null,
      characterOpenRequestId: 0,
      hasShownNoCampaignsDialog: false,

      arena: null,
      character: null,
      config: null,

      races: [],
      types: ['Character', 'NPC'],
      categories: [],
      characters: [],
      errors: [],
      errorFields: [],

      name: 'CharacterList',
      headings: [
        {
          id: 1,
          label: '',
          field: 'image',
          sortable: false,
          tdClass: 'text-center',
        },
        {
          id: 2,
          label: 'Name',
          field: 'name',
          width: '150px',
          tdClass: 'text-center',
        },
        {
          id: 27,
          label: 'Status',
          field: 'status_image',
          tdClass: 'text-center',
          sortable: false,
          filterable: false,
        },
        {
          id: 3,
          label: 'Player',
          field: 'player_name',
        },
        {
          id: 4,
          label: 'Category',
          field: 'category',
          tdClass: 'text-center',
        },
        {
          id: 5,
          label: 'Type',
          field: 'type',
          tdClass: 'text-center',
        },
        {
          id: 6,
          label: 'Race',
          field: 'race_name',
          tdClass: 'text-center',
        },
        {
          id: 7,
          label: 'Role',
          field: 'role',
          tdClass: 'text-center',
        },
        {
          id: 8,
          label: 'SR',
          field: 'sr_cur',
          type: 'number',
          tdClass: 'text-center',
        },
        {
          id: 9,
          label: 'CA',
          field: 'ca_cur',
          type: 'number',
          tdClass: 'text-center',
        },
        {
          id: 10,
          label: 'CR',
          field: 'cr_cur',
          type: 'number',
          tdClass: 'text-center',
        },
        {
          id: 11,
          label: 'Dam',
          field: this.hideZeros,
          tdClass: 'text-center',
        },
        {
          id: 12,
          label: 'STR',
          field: 'str_cur',
          type: 'number',
          tdClass: 'text-center',
          sortable: false,
        },
        {
          id: 13,
          label: 'CON',
          field: 'con_cur',
          type: 'number',
          tdClass: 'text-center',
          sortable: false,
        },
        {
          id: 14,
          label: 'DEX',
          field: 'dex_cur',
          type: 'number',
          tdClass: 'text-center',
          sortable: false,
        },
        {
          id: 15,
          label: 'SIZ',
          field: 'siz_cur',
          type: 'number',
          tdClass: 'text-center',
          sortable: false,
        },
        {
          id: 16,
          label: 'INT',
          field: 'int_cur',
          type: 'number',
          tdClass: 'text-center',
          sortable: false,
        },
        {
          id: 17,
          label: 'POW',
          field: 'pow_cur',
          type: 'number',
          tdClass: 'text-center',
          sortable: false,
        },
        {
          id: 18,
          label: 'CHA',
          field: 'cha_cur',
          type: 'number',
          tdClass: 'text-center',
          sortable: false,
        },
        {
          id: 19,
          label: 'Combat Inclusion',
          field: 'combat_inclusion_image',
          tdClass: 'text-center',
          filterable: false,
          sortable: false,
          formatFn: this.isAutoIncluded,
        },
        {
          id: 20,
          label: 'Deployed',
          field: 'in_combat_image',
          tdClass: 'text-center',
          filterable: false,
          sortable: false,
        },
        {
          id: 21,
          label: 'Combat Inclusion Flag',
          field: 'combat_inclusion',
          tdClass: 'text-center',
          filterable: false,
          sortable: false,
          formatFn: this.isAutoIncluded,
          hidden: true,
        },
        {
          id: 22,
          label: 'In-Combat Flag',
          field: 'in_combat',
          tdClass: 'text-center',
          filterable: false,
          sortable: false,
          hidden: true,
        },
        {
          id: 23,
          label: 'Deploy',
          field: 'action_deploy',
          filterable: false,
          sortable: false,
          tdClass: 'text-center',
          hidden: true,
        },
        {
          id: 24,
          label: 'Delete',
          field: 'action_delete',
          filterable: false,
          sortable: false,
          tdClass: 'text-center',
        },
        {
          id: 25,
          label: 'Id',
          field: 'id',
          type: 'number',
          tdClass: 'text-center',
          hidden: true,
        },
        {
          id: 26,
          label: 'Slug',
          field: 'slug',
          tdClass: 'text-center',
          hidden: true,
        },
      ],
    }
  },
  watch: {
    isSheetOpen(val) {
      if (val) this.sheetOpenCounter++
    },
  },
  //========================================================
  // computed
  //----------
  computed: {
    api_request() {
      return window.bbAPIRequest(this.endpoint, this.local_slug, this.deleteInd)
    },
    showLoader() {
      return !this.loadReady
    },
    isInvertedSidePanelOpen() {
      return !this.isSheetOpen
    },
    isMyCharactersScope() {
      return this.listScope === 'mine'
    },
    isCurrentCampaignDm() {
      const currentUser = this.$store.getters['auth/getUserProfile'] || {}
      const currentUserName = currentUser?.user_name || ''
      const dmUserName = this.arena?.dm?.user_name || ''

      return Boolean(
        currentUserName && dmUserName && currentUserName === dmUserName,
      )
    },
    canDeployCharacters() {
      return !this.isMyCharactersScope && this.isCurrentCampaignDm
    },
    tableHeadings() {
      return this.headings.map((heading) => {
        if (heading.field !== 'action_deploy') {
          return heading
        }

        return {
          ...heading,
          hidden: !this.canDeployCharacters,
        }
      })
    },
    allowCharacterAdd() {
      return true
    },
  },
  //========================================================
  // methods
  //---------
  methods: {
    //--------------------
    // ref mapped methods
    //--------------------
    ...mapMutations('auth', {
      setCurrentView: 'setCurrentView',
      setMessage: 'setMessage',
      destroyToken: 'destroyToken',
      destroyUserProfile: 'destroyUserProfile',
    }),
    ...mapMutations('combat', {
      setPlayerNames: 'setPlayerNames',
    }),
    ...mapMutations('characters', {
      setCharacters: 'setCharacters',
      resetCurrentCharacter: 'resetCurrentCharacter',
    }),
    ...mapGetters('auth', {
      getHeaders: 'getHeaders',
      getUserProfile: 'getUserProfile',
    }),
    ...mapGetters('combat', {
      getCampaign: 'getCampaign',
      getConfig: 'getConfig',
      getCampaignPlayers: 'getCampaignPlayers',
    }),
    ...mapGetters('characters', {
      getCharacters: 'getCharacters',
    }),
    ...mapActions('combat', {
      loadConfig: 'loadConfig',
      loadCampaignPlayers: 'loadCampaignPlayers',
    }),
    ...mapActions('races', {
      loadStaticCharacterRaces: 'loadStaticCharacterRaces',
      loadStaticCharacteristics: 'loadStaticCharacteristics',
    }),
    ...mapActions('characters', {
      loadCampaignCharacters: 'loadCampaignCharacters',
      loadMyCharacters: 'loadMyCharacters',
    }),
    //========================================================
    // configure headings for list scope
    //-----------------------------------
    configureHeadingsForScope() {
      const playerHeading = this.headings.find(
        (heading) =>
          heading.field === 'player_name' || heading.field === 'arena_name',
      )

      if (playerHeading) {
        playerHeading.label = this.isMyCharactersScope ? 'Campaign' : 'Player'
        playerHeading.field = this.isMyCharactersScope
          ? 'arena_name'
          : 'player_name'
        playerHeading.width = this.isMyCharactersScope ? '180px' : '160px'
      }
    },
    //========================================================
    // show character sheet panel
    //----------------------------
    showCharacterSheetPanel() {
      this.character = null
      this.isSheetOpen = this.isInvertedSidePanelOpen
    },
    //========================================================
    // is auto included in combat
    //----------------------------
    isAutoIncluded(value) {
      if (value) {
        return 'Yes'
      } else {
        return ''
      }
    },
    //========================================================
    // hide zeros
    //------------
    hideZeros(rowObj) {
      if (rowObj.damage_mod_melee_cur == '0') {
        return ''
      } else {
        return rowObj.damage_mod_melee_cur
      }
    },
    //========================================================
    // handle characters close
    //-------------------------
    handleCharactersClose() {
      this.SetView('')
      this.$router.go(-1)
    },
    //========================================================
    // get campaign handler
    //-------------------
    getCampaignHandler() {
      this.arena = this.getCampaign()
    },
    //========================================================
    // character view
    //----------------
    SetView(view) {
      this.setCurrentView(view)
    },
    async maybeShowNoCampaignsDialog() {
      if (
        !this.isMyCharactersScope ||
        this.hasShownNoCampaignsDialog ||
        !this.$refs.confirmDialog
      ) {
        return
      }

      const headers = this.getHeaders() || {}

      try {
        const response = await axios.get('api/v1/campaigns/member/?limit=1', {
          headers,
          mode: 'same-origin',
        })
        const hasCampaigns =
          (typeof response?.data?.count === 'number' &&
            response.data.count > 0) ||
          (Array.isArray(response?.data?.results) &&
            response.data.results.length > 0)

        if (!hasCampaigns) {
          this.hasShownNoCampaignsDialog = true
          await this.$refs.confirmDialog.show({
            title: 'Unable to create characters',
            message: 'Not enrolled in a campaign',
            okButton: 'OK',
          })
        }
      } catch (err) {
        console.log(
          'Failed to determine campaign enrolment for My Characters',
          err,
        )
      }
    },
    async resolveCampaignForSave(parms) {
      const payload = parms?.changed_profile?.payload || {}
      const targetArenaId =
        payload.arena ?? parms?.local_character?.arena ?? null
      const targetArenaName =
        payload.arena_name || parms?.local_character?.arena_name || null

      const currentCampaign = this.getCampaign?.() || this.arena || null
      if (
        currentCampaign?.slug &&
        ((targetArenaId != null &&
          String(currentCampaign.id) === String(targetArenaId)) ||
          (!isEmpty(targetArenaName) &&
            String(currentCampaign.name) === String(targetArenaName)))
      ) {
        return currentCampaign
      }

      let nextUrl = 'api/v1/campaigns/member/'
      const headers = this.getHeaders() || {}

      while (nextUrl) {
        const response = await axios.get(nextUrl, {
          headers,
          mode: 'same-origin',
        })
        const results = Array.isArray(response?.data?.results)
          ? response.data.results
          : []
        const campaign = results.find(
          (entry) =>
            String(entry.id) === String(targetArenaId) ||
            String(entry.name) === String(targetArenaName),
        )

        if (campaign?.slug) {
          return campaign
        }

        nextUrl = response?.data?.next ?? null
      }

      return null
    },
    async resolveMembershipIdForSave(parms) {
      const payload = parms?.changed_profile?.payload

      if (!payload || !isEmpty(payload.player) || isEmpty(payload.arena)) {
        return
      }

      const headers = this.getHeaders() || {}
      const userProfile = this.getUserProfile() || {}
      const playerName =
        payload.player_name ||
        parms?.local_character?.player_name ||
        userProfile.first_name ||
        userProfile.user_name ||
        ''

      const campaign = await this.resolveCampaignForSave(parms)
      const campaignSlug = campaign?.slug || null

      if (!campaignSlug) {
        return
      }

      const membershipResponse = await axios.get(
        `api/v1/campaigns/${campaignSlug}/players/`,
        {
          headers,
          mode: 'same-origin',
        },
      )
      const memberships = Array.isArray(membershipResponse?.data?.results)
        ? membershipResponse.data.results
        : []
      const candidateValues = [
        userProfile.id,
        userProfile.email,
        userProfile.user_name,
        userProfile.first_name,
        playerName,
      ]
        .filter((value) => !isEmpty(value))
        .map((value) => String(value).toLowerCase())

      const membership = memberships.find((entry) => {
        if (String(entry?.arena) !== String(payload.arena)) {
          return false
        }

        const membershipValues = [
          entry?.player,
          entry?.player_email,
          entry?.player_name,
          entry?.user_name,
          entry?.first_name,
        ]
          .filter((value) => !isEmpty(value))
          .map((value) => String(value).toLowerCase())

        return candidateValues.some((candidate) =>
          membershipValues.includes(candidate),
        )
      })

      if (membership?.id) {
        payload.player = membership.id
      }
    },
    async hasDuplicateCharacterNameBeforeSave(parms) {
      const payload = parms?.changed_profile?.payload || {}
      const targetName = String(payload.name || parms?.name || '')
        .trim()
        .toLowerCase()

      if (!targetName) {
        return false
      }

      const targetId =
        payload.id ?? parms?.character_id ?? parms?.local_character?.id ?? null
      const targetSlug = payload.slug ?? parms?.local_character?.slug ?? null
      const targetArenaId =
        payload.arena ?? parms?.local_character?.arena ?? null
      const targetArenaName =
        payload.arena_name || parms?.local_character?.arena_name || null

      const isSameCharacter = (character) => {
        if (!character) {
          return false
        }

        if (
          targetId != null &&
          character.id != null &&
          String(character.id) === String(targetId)
        ) {
          return true
        }

        if (
          targetSlug &&
          character.slug &&
          String(character.slug) === String(targetSlug)
        ) {
          return true
        }

        return false
      }

      const matchesTargetArena = (character) => {
        if (!character) {
          return false
        }

        if (targetArenaId != null && character.arena != null) {
          return String(character.arena) === String(targetArenaId)
        }

        if (!isEmpty(targetArenaName) && !isEmpty(character.arena_name)) {
          return String(character.arena_name) === String(targetArenaName)
        }

        return false
      }

      const localDuplicate = (
        Array.isArray(this.characters) ? this.characters : []
      ).find(
        (character) =>
          !isSameCharacter(character) &&
          matchesTargetArena(character) &&
          String(character?.name || '')
            .trim()
            .toLowerCase() === targetName,
      )

      if (localDuplicate) {
        return true
      }

      const campaign = await this.resolveCampaignForSave(parms)
      if (!campaign?.slug) {
        return false
      }

      const headers = this.getHeaders() || {}
      let nextUrl = `api/v1/campaigns/${campaign.slug}/characters/`

      while (nextUrl) {
        const response = await axios.get(nextUrl, {
          headers,
          mode: 'same-origin',
        })
        const results = Array.isArray(response?.data?.results)
          ? response.data.results
          : []

        const campaignDuplicate = results.find(
          (character) =>
            !isSameCharacter(character) &&
            String(character?.name || '')
              .trim()
              .toLowerCase() === targetName,
        )

        if (campaignDuplicate) {
          return true
        }

        nextUrl = response?.data?.next ?? null
      }

      return false
    },
    extractCharacterSaveErrorMessage(error) {
      const responseData = error?.response?.data

      if (typeof responseData === 'string' && responseData.trim()) {
        return responseData.trim()
      }

      if (Array.isArray(responseData?.name) && responseData.name[0]) {
        return responseData.name[0]
      }

      if (
        Array.isArray(responseData?.non_field_errors) &&
        responseData.non_field_errors[0]
      ) {
        return responseData.non_field_errors[0]
      }

      if (responseData && typeof responseData === 'object') {
        const firstFieldError = Object.values(responseData).find(
          (value) => Array.isArray(value) && value[0],
        )

        if (Array.isArray(firstFieldError) && firstFieldError[0]) {
          return firstFieldError[0]
        }
      }

      return error && error.message ? error.message : 'Unknown error'
    },
    //========================================================
    // handle character save (add/update)
    //------------------------------------
    async handleCharacterSave(parms) {
      await this.resolveMembershipIdForSave(parms)

      if (await this.hasDuplicateCharacterNameBeforeSave(parms)) {
        showNotification(
          'Save Error: Character name already taken for this campaign.',
        )
        return
      }

      // const saveParms = window.$deepCopy(parms)
      // const originalImageFile =
      //   parms?.changed_profile?.payload?.image_file ?? null

      // if (originalImageFile) {
      //   if (!saveParms.changed_profile) {
      //     saveParms.changed_profile = {}
      //   }
      //   if (!saveParms.changed_profile.payload) {
      //     saveParms.changed_profile.payload = {}
      //   }
      //   saveParms.changed_profile.payload.image_file = originalImageFile
      // }

      console.log('IN handleCharacterSave')
      console.log('parms = ', parms)
      // console.log('saveParms = ', saveParms)

      console.log('*********************')
      console.log('*** COMMIT STARTS ***')
      console.log('*********************')
      this.loadReady = false
      this.handleSheetClose()

      try {
        await useCharacterChangesHarness(parms)
        console.log('BACK FROM useCharacterChangesHarness')
        await this.loadCharactersHandler()
        //---------------------------
        // show confirmation message
        //---------------------------
        showNotification(
          'Character ' +
            this.formatCharacterNotificationName(parms.name) +
            ' ' +
            this.saveTypeMsg({ id: parms.character_id }),
        )
      } catch (error) {
        console.log('Error occurred while handling changes:', error)

        if (error?.response?.status === 400) {
          const errMsg = this.extractCharacterSaveErrorMessage(error)
          console.error(
            'Character save 400 response payload:',
            error?.response?.data,
          )
          showNotification('Error updating character: ' + errMsg, {
            duration: 12000,
          })
          return
        }

        handleError(error, 17)
        const errMsg = error && error.message ? error.message : 'Unknown error'
        this.loadReady = true
        console.error(
          'Character save error payload:',
          error?.response?.data || null,
        )
        showNotification('Error updating character: ' + errMsg, {
          duration: 12000,
        })
      } finally {
        this.loadReady = true
        this.resetCurrentCharacter()
      }
    },
    hydrateCharacterForList(character) {
      if (!character) {
        return null
      }

      let hydratedCharacters = [window.$deepCopy(character)]
      hydratedCharacters = loadCharacterCharacteristicProps(hydratedCharacters)
      hydratedCharacters = loadCharacterCharacteristicValues(hydratedCharacters)

      return hydratedCharacters[0] ?? null
    },
    async fetchCharacterDetails(slug) {
      if (!slug) {
        return null
      }

      const headers = this.getHeaders() || {}
      const response = await axios.get(`api/v1/characters/${slug}/`, {
        headers,
        mode: 'same-origin',
      })

      return response?.data ?? null
    },
    applySavedCharacterLocally({
      localCharacter = null,
      persistedCharacter = null,
      persistedImage = null,
      persistedCharacterHistory = null,
    } = {}) {
      const normalizedPersistedImage =
        persistedImage?.image ??
        persistedImage?.data?.image ??
        persistedImage ??
        null

      const normalizedPersistedCharacterHistory =
        persistedCharacterHistory?.character_history ??
        persistedCharacterHistory?.data?.character_history ??
        persistedCharacterHistory ??
        null

      const baseCharacter = localCharacter
        ? window.$deepCopy(localCharacter)
        : null

      if (!baseCharacter && !persistedCharacter) {
        return
      }

      const savedCharacter = {
        ...(baseCharacter || {}),
        ...(persistedCharacter ? window.$deepCopy(persistedCharacter) : {}),
      }

      if (persistedCharacter?.id != null) {
        savedCharacter.id = persistedCharacter.id
      }

      if (persistedCharacter?.slug) {
        savedCharacter.slug = persistedCharacter.slug
      }

      if (normalizedPersistedImage) {
        savedCharacter.image = normalizedPersistedImage
      }

      if (normalizedPersistedCharacterHistory !== null) {
        savedCharacter.character_history = normalizedPersistedCharacterHistory
      }

      const hydratedCharacter = this.hydrateCharacterForList(savedCharacter)

      if (!hydratedCharacter) {
        return
      }

      const characters = Array.isArray(this.characters)
        ? [...this.characters]
        : []

      const existingIndex = characters.findIndex((character) => {
        if (
          hydratedCharacter.id != null &&
          character?.id === hydratedCharacter.id
        ) {
          return true
        }

        if (
          hydratedCharacter.slug &&
          character?.slug === hydratedCharacter.slug
        ) {
          return true
        }

        return false
      })

      if (existingIndex === -1) {
        characters.push(hydratedCharacter)
      } else {
        characters.splice(existingIndex, 1, hydratedCharacter)
      }

      this.characters = refreshCharacterOrder(characters)
      this.setCharacters(this.characters)
    },
    //========================================================
    // save type message
    //-------------------
    saveTypeMsg({ id }) {
      console.log('*******************')
      console.log('*** COMMIT ENDS ***')
      console.log('*******************')
      console.log('IN saveTypeMsg = ', id)
      if (id !== 0) {
        return 'updated.'
      } else {
        return 'added.'
      }
    },
    formatCharacterNotificationName(name) {
      return capitaliseMultipleWords(name)
    },
    //========================================================
    // handle character add
    //----------------------
    async handleCharacterAdd() {
      console.log('-------------------------------')
      console.log('IN handleCharacterAdd')

      if (!this.getConfig()) {
        await this.loadConfigHandler()
      }

      this.character = null
      this.character_idx = 0
      this.isSheetOpen = true
    },
    //========================================================
    // handle character update
    //-------------------------
    async handleCharacterUpdate(parm) {
      console.log('-------------------------------')
      console.log('IN handleCharacterUpdate')

      const selectedCharacter = parm?.character ?? null
      const selectedSlug = selectedCharacter?.slug ?? null

      if (!selectedSlug) {
        return
      }

      if (this.openingCharacterSlug === selectedSlug) {
        return
      }

      this.loadReady = false
      this.openingCharacterSlug = selectedSlug
      const requestId = (this.characterOpenRequestId ?? 0) + 1
      this.characterOpenRequestId = requestId

      try {
        const fullCharacter = await this.fetchCharacterDetails(selectedSlug)

        if (this.characterOpenRequestId !== requestId || !fullCharacter) {
          return
        }

        this.character = fullCharacter
        this.character_idx = parm?.character_idx ?? parm?.idx ?? 0
        this.isSheetOpen = true
      } catch (err) {
        if (this.characterOpenRequestId === requestId) {
          handleError(err, 22)
        }
      } finally {
        if (this.characterOpenRequestId === requestId) {
          this.loadReady = true
          this.openingCharacterSlug = null
        }
      }
    },
    //========================================================
    // handle sheet close
    //--------------------
    handleSheetClose() {
      console.log('CharacterView - handleSheetClose')
      this.SetView('characters')
      this.isSheetOpen = false
      this.openingCharacterSlug = null
      this.character = null
      this.character_idx = 0
    },
    //========================================================
    // handle character deploy
    //-------------------------
    async handleCharacterDeploy(newCombatant) {
      if (!this.canDeployCharacters || !this.arena?.slug) {
        return
      }

      const sourceCharacter = this.characters.find(
        (character) => character?.id === newCombatant?.character,
      )

      const deployCharacter = await resolveCharacterForCombatDeploy(
        sourceCharacter ?? newCombatant,
      )

      const combatantToDeploy = convertCharacterToCombatant(deployCharacter)

      if (!combatantToDeploy) {
        return
      }

      //----------------------------
      // clear new combatant fields
      //----------------------------
      combatantToDeploy.id = null
      combatantToDeploy.slug = ''
      const copy_image = true
      //---------------------
      // call edit combatant
      //---------------------
      let call = useEditCombatant(combatantToDeploy, copy_image)
      await call
        .then(() => {
          // reload characters
          this.loadCharactersHandler()
          // show message
          showNotification('Character ' + combatantToDeploy.name + ' deployed.')
        })
        .catch((err) => {
          handleError(err, 16)
        })
    },
    //========================================================
    // handle character delete
    //--------------------------
    async handleCharacterDelete(delete_index) {
      console.log('-------------------------------')
      console.log('IN handleCharacterDelete')
      this.loadReady = false
      //---------------
      // get character
      //---------------
      const character = getCharacterByIndex(delete_index)
      //-----------------------
      // call delete combatant
      //-----------------------
      let call = useDeleteCharacter(character.slug)
      await call
        .then(() => {
          let msg = ''
          if (!isEmpty(character.combatant)) {
            msg =
              'Character & related Combatant ' + character.name + ' deleted.'
          } else {
            msg = 'Character ' + character.name + ' deleted.'
          }
          this.loadCharactersHandler()
          showNotification(msg)
        })
        .catch((err) => {
          handleError(err, 18)
        })
    },
    //========================================================
    // load config handler
    //---------------------
    async loadConfigHandler() {
      //----------
      // api call
      //----------
      await this.loadConfig()
        //------------
        // successful
        //------------
        .then(() => {
          this.config = this.getConfig()
          // load table heading dropdowns (categories)
          // this.headings[3].filterOptions.filterDropdownItems =
          // this.config.categories;
          // load table heading dropdowns (types)
          // this.headings[4].filterOptions.filterDropdownItems =
          //   this.config.character_types;
        })
        //-------
        // error
        //-------
        .catch((err) => {
          handleError(err, 19)
        })
    },
    //========================================================
    // load races handler
    //--------------------
    async loadRacesHandler() {
      //----------
      // api call
      //----------
      await this.loadStaticCharacterRaces()
        //------------
        // successful
        //------------
        .then(() => {})
        //-------
        // error
        //-------
        .catch((err) => {
          handleError(err, 20)
        })
    },
    //========================================================
    // load campaign players handler
    //----------------------------
    async loadCampaignPlayersHandler() {
      const slug = this.arena.slug
      //----------
      // api call
      //----------
      await this.loadCampaignPlayers(slug)
        //------------
        // successful
        //------------
        .then(() => {})
        //-------
        // error
        //-------
        .catch((err) => {
          handleError(err, 21)
        })
    },
    //========================================================
    // load campaign characters handler
    //-------------------------------
    async loadCampaignCharactersHandler(slug) {
      console.log('-------------------------------')
      console.log('IN loadArenaCharactersHandler')
      this.loadReady = false
      //---------------
      // make API call
      //---------------
      let api_call = this.loadCampaignCharacters(slug)
      //---------------
      // await results
      //---------------
      await api_call
        .then(() => {
          //-----------------------
          // get stored characters
          //-----------------------
          let characters = this.getCharacters()
          //---------------------------
          // load characteristic props
          //---------------------------
          this.characters = loadCharacterCharacteristicProps(characters)
          //----------------------------
          // load characteristic values
          //----------------------------
          this.characters = loadCharacterCharacteristicValues(this.characters)
          //-------------------------
          // refresh character order
          //-------------------------
          this.characters = refreshCharacterOrder(this.characters)
          console.log('xx characters = ', this.characters)
          //-------------------------------
          // refresh main/saved characters
          //-------------------------------
          this.setCharacters(this.characters)
          this.loadReady = true
        })
        .catch((err) => {
          handleError(err, 22)
        })
    },
    //========================================================
    // load my characters handler
    //---------------------------
    async loadMyCharactersHandler() {
      this.loadReady = false

      await this.loadMyCharacters()
        .then(() => {
          let characters = this.getCharacters()
          this.characters = loadCharacterCharacteristicProps(characters)
          this.characters = loadCharacterCharacteristicValues(this.characters)
          this.characters = refreshCharacterOrder(this.characters)

          console.log('yy characters = ', this.characters)
          this.setCharacters(this.characters)
          this.loadReady = true

          if (this.characters.length === 0) {
            this.maybeShowNoCampaignsDialog()
          }
        })
        .catch((err) => {
          handleError(err, 22)
        })
    },
    //========================================================
    // load characters for current scope
    //---------------------------------
    loadCharactersHandler() {
      if (this.isMyCharactersScope) {
        return this.loadMyCharactersHandler()
      }

      if (!this.arena?.slug) {
        this.characters = []
        this.setCharacters([])
        this.loadReady = true
        return Promise.resolve()
      }

      return this.loadCampaignCharactersHandler(this.arena.slug)
    },
  },
  //========================================================
  // on mount
  //----------
  mounted() {
    this.configureHeadingsForScope()
    //---------------------
    // load config handler
    //---------------------
    this.loadConfigHandler()
    //--------------------
    // load races handler
    //--------------------
    this.loadRacesHandler()
    //-------------------
    // get campaign handler
    //-------------------
    this.getCampaignHandler()
    //-----------------------------
    // load static characteristics
    //-----------------------------
    this.loadStaticCharacteristics()

    if (!this.isMyCharactersScope && this.arena) {
      //---------------------------
      // load campaign players handler
      //----------------------------
      this.loadCampaignPlayersHandler()
      //------------------
      // set player names
      //------------------
      let player_names = extractPlayerNames(this.arena.players)
      this.setPlayerNames(player_names)
    }

    //------------------------------
    // load characters for this mode
    //-------------------------------
    this.loadCharactersHandler()
    //--------------------
    // set character view
    //--------------------
    this.SetView('characters')
  },
}
</script>

<style scoped>
.bb-character-sheet-overlay {
  position: fixed;
  inset: 0;
  z-index: 200;
  background: #1e1e1e;
}
</style>
